import { AgentRuleBundle } from "../agent-config-options";
import { AGENT_MODEL, AgentModel } from "../types";
import { agendaBundle } from "./agenda";
import { awsCdkBundle } from "./aws-cdk";
import {
  buildBaseBundle,
  DEFAULT_BASE_CONVENTIONS,
  ResolvedBaseConventions,
} from "./base";
import { buildBcmWriterBundle } from "./bcm-writer";
import { DEFAULT_BUILD_POLICY, ResolvedBuildPolicy } from "./build-policy";
import { buildBusinessModelsBundle } from "./business-models";
import { buildCompanyProfileBundle } from "./company-profile";
import { buildCustomerProfileBundle } from "./customer-profile";
import { buildDocsSyncBundle } from "./docs-sync";
import { buildGithubWorkflowBundle } from "./github-workflow";
import { buildIndustryDiscoveryBundle } from "./industry-discovery";
import {
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  ResolvedIssueDefaults,
} from "./issue-defaults";
import { jestBundle } from "./jest";
import { buildMaintenanceAuditBundle } from "./maintenance-audit";
import { buildMeetingAnalysisBundle } from "./meeting-analysis";
import {
  buildOrchestratorBundle,
  DEFAULT_ORCHESTRATOR_CONVENTIONS,
  ResolvedOrchestratorConventions,
} from "./orchestrator";
import { DEFAULT_AGENT_PATHS, ResolvedAgentPaths } from "./paths";
import { buildPeopleProfileBundle } from "./people-profile";
import { pnpmBundle } from "./pnpm";
import { buildPrReviewBundle } from "./pr-review";
import {
  ResolvedPrReviewPolicy,
  resolvePrReviewPolicy,
} from "./pr-review-policy";
import { projenBundle } from "./projen";
import { buildRegulatoryResearchBundle } from "./regulatory-research";
import { buildRequirementsAnalystBundle } from "./requirements-analyst";
import { buildRequirementsReviewerBundle } from "./requirements-reviewer";
import { buildRequirementsWriterBundle } from "./requirements-writer";
import { buildResearchPipelineBundle } from "./research-pipeline";
import { slackBundle } from "./slack";
import { buildSoftwareProfileBundle } from "./software-profile";
import { buildStandardsResearchBundle } from "./standards-research";
import { buildTurborepoBundle } from "./turborepo";
import { typescriptBundle } from "./typescript";
import { upstreamConfigulatorDocsBundle } from "./upstream-configulator-docs";
import { vitestBundle } from "./vitest";

export {
  AGENT_REGISTRY_ENTRIES,
  SUPPRESSED_WORKFLOW_RULE_NAMES,
  buildAgentRegistryRule,
  bundleNameForWorkflowRule,
  isSuppressedWorkflowRule,
  type AgentRegistryEntry,
} from "./agent-registry";
export { agendaBundle } from "./agenda";
export { awsCdkBundle } from "./aws-cdk";
export {
  baseBundle,
  buildBaseBundle,
  DEFAULT_BASE_CONVENTIONS,
  type ResolvedBaseConventions,
} from "./base";
export {
  BUNDLE_OWNERSHIP,
  CONVENTIONAL_COMMIT_TYPE_LABELS,
  PHASE_LABEL_TYPE_MAP,
  hasAnyDocsEmittingBundle,
  hasAnyDownstreamIssueKindBundle,
  isPhaseLabelOwnedByExcluded,
  isScheduledTaskOwnedByExcluded,
  isTypeLabelOwnedByExcluded,
  renderPhaseTypeInvariantSection,
  renderPhaseTypeInvariantShellHelpers,
  resolveTypeLabelForLabels,
  typeLabelForPhaseLabel,
  type BundleOwnership,
  type PhaseLabelTypeOutcome,
  type PhaseLabelTypeResolution,
} from "./bundle-ownership";
export { buildBcmWriterBundle, bcmWriterBundle } from "./bcm-writer";
export {
  DEFAULT_BUILD_POLICY,
  resolveBuildPolicy,
  type ResolvedBuildPolicy,
} from "./build-policy";
export {
  buildBusinessModelsBundle,
  businessModelsBundle,
} from "./business-models";
export {
  checkClosingKeywordsProcedure,
  renderCheckClosingKeywordsScript,
} from "./closing-keywords";
export {
  buildCompanyProfileBundle,
  companyProfileBundle,
} from "./company-profile";
export {
  buildCustomerProfileBundle,
  customerProfileBundle,
} from "./customer-profile";
export {
  buildDocsSyncBundle,
  checkDocSamplesProcedure,
  checkLinksProcedure,
  docsSyncBundle,
  extractApiProcedure,
  renderCheckDocSamplesProcedure,
  renderCheckLinksProcedure,
  renderExtractApiProcedure,
  renderStripToolArtifactTagsProcedure,
  stripToolArtifactTagsProcedure,
} from "./docs-sync";
export {
  buildGithubWorkflowBundle,
  githubWorkflowBundle,
} from "./github-workflow";
export {
  buildIndustryDiscoveryBundle,
  industryDiscoveryBundle,
} from "./industry-discovery";
export { jestBundle } from "./jest";
export {
  buildMaintenanceAuditBundle,
  maintenanceAuditBundle,
} from "./maintenance-audit";
export {
  DEFAULT_ISSUE_PRIORITY,
  DEFAULT_ISSUE_STATUS,
  DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  VALID_PRIORITY_VALUES,
  VALID_STATUS_VALUES,
  labelsForPhase,
  resolveIssueDefaults,
  validateIssueDefaultsConfig,
  type IssueDefaultsPriority,
  type IssueDefaultsStatus,
  type ResolvedIssueDefaults,
  type ResolvedIssueDefaultsEntry,
} from "./issue-defaults";
export {
  buildMeetingAnalysisBundle,
  meetingAnalysisBundle,
} from "./meeting-analysis";
export {
  buildCheckBlockedProcedure,
  buildOrchestratorBundle,
  buildOrchestratorConventionsContent,
  buildUnblockDependentsProcedure,
  DEFAULT_ORCHESTRATOR_CONVENTIONS,
  orchestratorBundle,
  resolveOrchestratorAssets,
  type ResolvedOrchestratorConventions,
} from "./orchestrator";
export {
  DEFAULT_AGENT_PATHS,
  DEFAULT_REQUIREMENT_CATEGORY_DIRS,
  resolveAgentPaths,
  type ResolvedAgentPaths,
  type ResolvedRequirementCategoryDirs,
} from "./paths";
export {
  buildPeopleProfileBundle,
  peopleProfileBundle,
} from "./people-profile";
export { pnpmBundle } from "./pnpm";
export {
  DEFAULT_ISSUE_TEMPLATES_BUNDLE_PATH_PATTERNS,
  DEFAULT_ISSUE_TEMPLATES_EMIT_CHECKER,
  DEFAULT_ISSUE_TEMPLATES_EMIT_STARTER,
  DEFAULT_ISSUE_TEMPLATES_ENABLED,
  DEFAULT_ISSUE_TEMPLATES_PATH,
  DEFAULT_ISSUE_TEMPLATES_REQUIRE_REFERENCE,
  ISSUE_TEMPLATES_GENERATED_SUFFIX,
  collectIssueTemplateRecipeStubs,
  issueTemplatesChildGlob,
  issueTemplatesGeneratedPath,
  renderIssueTemplateLabelsCheckerScript,
  renderIssueTemplatesBundleHook,
  renderIssueTemplatesCheckerScript,
  renderIssueTemplatesGeneratedPage,
  renderIssueTemplatesRuleContent,
  renderIssueTemplatesStarterPage,
  resolveIssueTemplates,
  validateIssueTemplatesConfig,
  type IssueTemplateRecipeStub,
  type ResolvedIssueTemplates,
} from "./issue-templates";
export {
  DEFAULT_GITHUB_ISSUE_TYPE,
  GITHUB_ISSUE_TYPES,
  GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX,
  SET_ISSUE_TYPE_HELPER_PATH,
  githubIssueTypeForTitle,
  renderGithubIssueTypeSection,
  renderGithubIssueTypeSectionLines,
  renderIssueTypeAssignmentBlanket,
  renderIssueTypeAssignmentStep,
  renderSetIssueTypeFallbackLines,
  renderTitlePrefixTypeBullets,
  type GithubIssueType,
  type IssueTypeAssignmentStepOptions,
} from "./issue-type";
export {
  DEFAULT_PROGRESS_FILES_ENABLED,
  DEFAULT_PROGRESS_FILES_FILENAME_PATTERN,
  DEFAULT_PROGRESS_FILES_FORMAT,
  DEFAULT_PROGRESS_FILES_STALE_AFTER_HOURS,
  DEFAULT_PROGRESS_FILES_STATE_DIR,
  PROGRESS_FILES_FORMAT_VALUES,
  renderProgressFileName,
  renderProgressFilePath,
  renderProgressFilesBundleHook,
  renderProgressFilesRuleContent,
  resolveProgressFiles,
  validateProgressFilesConfig,
  type ResolvedProgressFiles,
} from "./progress-files";
export { buildPrReviewBundle, prReviewBundle } from "./pr-review";
export {
  DEFAULT_PATHS_EXEMPT_FROM_SIZE,
  resolvePrReviewPolicy,
  validatePrReviewPolicyConfig,
  type ResolvedPrReviewPolicy,
  type ResolvedPrReviewAutoMerge,
} from "./pr-review-policy";
export { projenBundle } from "./projen";
export {
  buildRegulatoryResearchBundle,
  regulatoryResearchBundle,
} from "./regulatory-research";
export {
  renderCustomDocSectionBlock,
  renderCustomDocSections,
  renderSourceTierExamples,
} from "./features";
export { renderFocusSection } from "./focus";
export { renderMeetingTypesSection } from "./meeting-types";
export { renderPriorityRulesSection } from "./priority-rules";
export {
  DEFAULT_DISPATCH_MODEL,
  DEFAULT_DISPATCH_TO_HOUSEKEEPING_RATIO,
  DEFAULT_HOUSEKEEPING_MODEL,
  DEFAULT_STATE_FILE_PATH,
  classifyRun,
  renderRunRatioSection,
  renderRunRatioShellHelpers,
  resolveRunRatio,
  validateRunRatioConfig,
  type ResolvedRunRatio,
} from "./run-ratio";
export {
  DEFAULT_AC_THRESHOLDS,
  DEFAULT_BUNDLE_OVERRIDES,
  DEFAULT_DECOMPOSITION_TEMPLATE,
  DEFAULT_SOURCES_THRESHOLDS,
  SCOPE_CLASS_VALUES,
  classifyIssueScope,
  renderScopeGateSection,
  renderScopeGateShellHelpers,
  resolveOverrideForLabels,
  resolveScopeGate,
  validateScopeGateConfig,
  type EffectiveScopeThresholds,
  type ResolvedScopeGate,
  type ResolvedScopeGateBundleOverride,
  type ScopeClass,
} from "./scope-gate";
export {
  DEFAULT_OFF_PEAK_CRON_EXAMPLE,
  DEFAULT_SCHEDULED_TASKS_ROOT,
  DEFAULT_SCHEDULED_TASK_ENTRIES,
  SCHEDULED_TASK_MODEL_VALUES,
  renderScheduledTaskSkillFile,
  renderScheduledTasksSection,
  resolveScheduledTasks,
  validateScheduledTasksConfig,
  type ResolvedScheduledTask,
  type ResolvedScheduledTasks,
  type ScheduledTaskModel,
} from "./scheduled-tasks";
export {
  AGENT_TIER_ROLES,
  AGENT_TIER_VALUES,
  DEFAULT_AGENT_TIERS,
  UNKNOWN_TYPE_FALLBACK_TIER,
  renderAgentTierCaseStatement,
  renderAgentTierSection,
  resolveAgentTiers,
  validateAgentTierConfig,
  type AgentTier,
  type ResolvedAgentTier,
} from "./tiers";
export {
  DEFAULT_PARTIAL_UNBLOCK_COMMENT_TEMPLATE,
  DEFAULT_UNBLOCK_COMMENT_TEMPLATE,
  DEFAULT_UNBLOCK_DEPENDENTS_ENABLED,
  renderUnblockDependentsScript,
  renderUnblockDependentsSection,
  resolveUnblockDependents,
  validateUnblockDependentsConfig,
  type ResolvedUnblockDependents,
} from "./unblock-dependents";
export {
  DEFAULT_SHARED_EDITING_CONFLICT_STRATEGY,
  DEFAULT_SHARED_EDITING_EMIT_HELPER,
  DEFAULT_SHARED_EDITING_ENABLED,
  DEFAULT_SHARED_EDITING_VERIFY_COMMIT,
  DEFAULT_SHARED_INDEX_PATHS,
  SHARED_EDITING_CONFLICT_STRATEGY_VALUES,
  renderSharedEditingBundleHook,
  renderSharedEditingHelperScript,
  renderSharedEditingRuleContent,
  resolveSharedEditing,
  validateSharedEditingConfig,
  type ResolvedSharedEditing,
} from "./shared-editing";
export {
  DEFAULT_PRODUCT_CONTEXT_PATH,
  DEFAULT_REQUIRE_PRODUCT_CONTEXT,
  DEFAULT_SKILL_EVALS_EMIT_RUNNER,
  DEFAULT_SKILL_EVALS_ENABLED,
  DEFAULT_SKILL_EVALS_SKILLS_ROOT,
  renderSkillEvalsBundleHook,
  renderSkillEvalsRuleContent,
  renderSkillEvalsRunnerScript,
  resolveSkillEvals,
  validateSkillEvalsConfig,
  type ResolvedSkillEvals,
} from "./skill-evals";
export {
  DEFAULT_TEMPORAL_FRAMING_CADENCES,
  DEFAULT_TEMPORAL_FRAMING_EMIT_CHECKER,
  DEFAULT_TEMPORAL_FRAMING_ENABLED,
  DEFAULT_TEMPORAL_FRAMING_PATHS,
  TEMPORAL_FRAMING_CATEGORY_VALUES,
  renderTemporalFramingCheckerScript,
  renderTemporalFramingRuleContent,
  resolveTemporalFraming,
  validateTemporalFramingConfig,
  type ResolvedTemporalFraming,
  type TemporalFramingCategory,
} from "./temporal-framing";
export {
  buildRequirementsAnalystBundle,
  requirementsAnalystBundle,
} from "./requirements-analyst";
export {
  buildRequirementsReviewerBundle,
  requirementsReviewerBundle,
} from "./requirements-reviewer";
export {
  buildRequirementsWriterBundle,
  nextRequirementIdProcedure,
  REQUIREMENTS_WRITER_PATHS,
  renderNextRequirementIdProcedure,
  requirementsWriterBundle,
} from "./requirements-writer";
export {
  buildResearchPipelineBundle,
  researchPipelineBundle,
} from "./research-pipeline";
export { slackBundle } from "./slack";
export {
  buildSoftwareProfileBundle,
  softwareProfileBundle,
} from "./software-profile";
export {
  buildStandardsResearchBundle,
  standardsResearchBundle,
} from "./standards-research";
export { buildTurborepoBundle, turborepoBundle } from "./turborepo";
export { typescriptBundle } from "./typescript";
export {
  DEFAULT_UPSTREAM_CONFIGULATOR_ENABLED,
  upstreamConfigulatorDocsBundle,
} from "./upstream-configulator-docs";
export { vitestBundle } from "./vitest";

/**
 * Resolved settings for every **config-driven convention rule** — a
 * bundle rule whose body is derived from consumer configuration rather
 * than being fixed prose.
 *
 * These rules used to ship default content from their bundle and get
 * rewritten late in `AgentConfig.resolveRules()`, which silently
 * discarded any `ruleExtensions` append or same-name `rules` override
 * that had already been merged into the map. Passing the resolved
 * settings down to the bundle factories instead keeps the rule map a
 * pure composition surface: seed once, then extend / override / exclude
 * on top.
 */
export interface ResolvedRuleConventions {
  /** Settings for the five convention rules the `base` bundle owns. */
  readonly base: ResolvedBaseConventions;
  /** Settings for the `orchestrator-conventions` rule. */
  readonly orchestrator: ResolvedOrchestratorConventions;
}

/**
 * Convention settings applied when the consumer configures none of the
 * config-driven conventions.
 */
export const DEFAULT_RULE_CONVENTIONS: ResolvedRuleConventions = {
  base: DEFAULT_BASE_CONVENTIONS,
  orchestrator: DEFAULT_ORCHESTRATOR_CONVENTIONS,
};

/**
 * Build the full list of built-in rule bundles with the supplied
 * resolved agent paths. Every path-aware bundle accepts a
 * `ResolvedAgentPaths` and threads it through its rule / skill /
 * sub-agent content, so a consumer override of
 * `AgentConfigOptions.paths` propagates into the rendered output.
 *
 * Bundles that file downstream issues also accept a
 * `ResolvedIssueDefaults` second argument so per-phase-label
 * overrides supplied via `AgentConfigOptions.issueDefaults`
 * propagate into every filing site's `gh issue create` prose.
 * When the consumer supplies no override, the bundle defaults
 * (`status:ready` + `priority:medium`) ship unchanged.
 *
 * Bundles that describe the build (`turborepo`, `github-workflow`)
 * accept a `ResolvedBuildPolicy` so the rendered guidance matches the
 * consumer's Turborepo configuration — most importantly, the
 * AWS-authentication note is emitted only when a remote cache is
 * actually configured, and names that consumer's own profile.
 *
 * Order matters: base is first so its rules can be overridden by
 * more specific bundles. The base bundle's `appliesWhen` always
 * returns true; it is filtered by the `includeBaseRules` option
 * in AgentConfig.
 *
 * Bundles that own a **config-driven convention rule** (`base`,
 * `orchestrator`) accept their resolved convention settings via
 * `conventions` so each rule's content is final the moment it enters
 * the rule map. Seeding resolved content up front — instead of
 * rewriting the rule after the map is assembled — is what lets
 * `AgentConfigOptions.ruleExtensions` appends and consumer-supplied
 * same-name `rules` entries compose with a convention override rather
 * than being clobbered by it.
 *
 * Bundles that do not read any agent path (typescript, jest,
 * pnpm, etc.) stay as const exports and are referenced unchanged.
 */
export function buildBuiltInBundles(
  paths: ResolvedAgentPaths = DEFAULT_AGENT_PATHS,
  issueDefaults: ResolvedIssueDefaults = DEFAULT_RESOLVED_ISSUE_DEFAULTS,
  defaultAgentTier: AgentModel = AGENT_MODEL.BALANCED,
  bundleAgentTiers: ReadonlyMap<string, AgentModel> = new Map(),
  prReviewPolicy: ResolvedPrReviewPolicy = resolvePrReviewPolicy(),
  buildPolicy: ResolvedBuildPolicy = DEFAULT_BUILD_POLICY,
  conventions: ResolvedRuleConventions = DEFAULT_RULE_CONVENTIONS,
): ReadonlyArray<AgentRuleBundle> {
  const tierFor = (bundle: string): AgentModel =>
    bundleAgentTiers.get(bundle) ?? defaultAgentTier;

  return [
    buildBaseBundle(paths, conventions.base),
    upstreamConfigulatorDocsBundle,
    typescriptBundle,
    vitestBundle,
    jestBundle,
    buildTurborepoBundle(buildPolicy),
    pnpmBundle,
    awsCdkBundle,
    projenBundle,
    buildGithubWorkflowBundle(buildPolicy),
    slackBundle,
    buildMeetingAnalysisBundle(tierFor("meeting-analysis")),
    agendaBundle,
    buildOrchestratorBundle(conventions.orchestrator),
    buildPrReviewBundle(prReviewPolicy),
    buildRequirementsAnalystBundle(paths, issueDefaults),
    buildRequirementsWriterBundle(paths, issueDefaults),
    buildRequirementsReviewerBundle(paths, issueDefaults),
    buildResearchPipelineBundle(paths, issueDefaults),
    buildCompanyProfileBundle(paths, issueDefaults, tierFor("company-profile")),
    buildCustomerProfileBundle(
      paths,
      issueDefaults,
      tierFor("customer-profile"),
    ),
    buildPeopleProfileBundle(paths, issueDefaults, tierFor("people-profile")),
    buildSoftwareProfileBundle(
      paths,
      issueDefaults,
      tierFor("software-profile"),
    ),
    buildIndustryDiscoveryBundle(paths, issueDefaults),
    buildBusinessModelsBundle(paths, issueDefaults),
    buildBcmWriterBundle(paths, issueDefaults),
    buildStandardsResearchBundle(paths, issueDefaults),
    buildRegulatoryResearchBundle(paths, issueDefaults),
    buildMaintenanceAuditBundle(
      paths,
      issueDefaults,
      tierFor("maintenance-audit"),
    ),
    buildDocsSyncBundle(paths),
  ];
}

/**
 * Built-in rule bundles assembled with the default agent paths.
 * Preserved for backward compatibility with tests and consumers
 * that import the array directly. Prefer `buildBuiltInBundles(paths)`
 * when consumer overrides are in scope.
 */
export const BUILT_IN_BUNDLES: ReadonlyArray<AgentRuleBundle> =
  buildBuiltInBundles();
